import { reportError } from "$lib/errorHandling/errorReporter";
import { logger } from "$lib/utils/logger";
import { createStorage } from "./localStorageFallback";

export class CacheStorageManager<T> {
  private storage: Storage;
  private storageKey: string;
  private storageIsAvailable: boolean = false;
  private defaultItem = JSON.stringify([] as Array<T>);

  // currently only supports single storage item for simplicity
  constructor(storageKey?: string) {
    this.storageKey = storageKey ?? "defaultStorageManagerKey";
    this.storageIsAvailable = this.checkIsStorageAvailable();
    // if you add @vitest-environment jsdom to your test file, then window.localStorage is available
    this.storage = this.storageIsAvailable ? localStorage : createStorage();
  }

  private checkIsStorageAvailable(): boolean {
    const useLocalStorage =
      typeof window !== "undefined" && !!window.localStorage;
    return useLocalStorage;
  }

  getCache(): Array<T> {
    let item = this.storage.getItem(this.storageKey);
    if (!item) {
      this.storage.setItem(this.storageKey, this.defaultItem);
      item = this.defaultItem;
    }
    const cacheArray: Array<T> = JSON.parse(item);
    if (Array.isArray(cacheArray)) {
      return cacheArray;
    }
    throw new Error(
      `[StorageManager] Cache is not an array. Key: ${this.storageKey}`
    );
  }

  private setCache(value: Array<T>): void {
    try {
      this.storage.setItem(this.storageKey, JSON.stringify(value));
    } catch (error) {
      reportError(
        `[StorageManager] Failed to set cache. Key: ${this.storageKey}`,
        error
      );
    }
  }

  appendToCache(cacheItem: T): void {
    logger.debug(
      `[StorageManager] Appending item to cache. Key: ${this.storageKey}, Item:`,
      cacheItem
    );
    const currentCache = this.getCache();
    currentCache.push(cacheItem);
    this.setCache(currentCache);
  }

  removeFromCache(cacheItem: T): void {
    logger.debug(
      `[StorageManager] Removing item from cache. Key: ${this.storageKey}, Item:`,
      cacheItem
    );
    const popCache = this.getCache();
    const index = popCache.findIndex(
      (item) => JSON.stringify(item) === JSON.stringify(cacheItem)
    );
    if (index > -1) {
      popCache.splice(index, 1);
      logger.debug("[StorageManager] Cache after removal:", popCache);
      this.setCache(popCache);
    } else {
      logger.warn(
        `[StorageManager] Item not found in cache. Key: ${this.storageKey}, Item:`,
        cacheItem
      );
    }
  }

  removeCache(): void {
    this.storage.removeItem(this.storageKey);
  }

  clear(): void {
    this.storage.clear();
  }

  setStorage(storage: Storage): void {
    this.storage = storage;
  }
}
